// Uploaded pictures, the map and the view, as the client uses them (slice 06,
// issue 746). A picture's bytes are fetched WITH the bearer — never a URL a
// page could hand to anyone — and turned into an object URL the board makes
// a texture from; the URL lives in this tab's memory and dies with it.
import type { AssetInfo, AssetKind, AssetUse, BoardView, LibrarySort, TableView } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { authFetch } from './session';
import type { TableResult } from './table';

export type UploadResult =
  | { kind: 'ok'; asset: AssetInfo; warning: string | null }
  | { kind: 'refused'; reason: string }
  | { kind: 'error' };

export async function uploadPicture(kind: AssetKind, name: string, file: Blob, fetchImpl: typeof fetch = fetch): Promise<UploadResult> {
  try {
    const res = await authFetch(`/api/assets?kind=${kind}&name=${encodeURIComponent(name)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream', Accept: 'application/json' },
      body: file,
    }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { asset?: AssetInfo; warning?: string | null; error?: string; reason?: string } | null;
    if (res.status === 201 && body?.asset) return { kind: 'ok', asset: body.asset, warning: body.warning ?? null };
    if (body?.error === 'refused' && typeof body.reason === 'string') return { kind: 'refused', reason: body.reason };
    if (res.status === 403) return { kind: 'refused', reason: 'Only a DM uploads pictures.' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

/**
 * A hot bar box's own picture (qa-runs#369 slice B; design/23): uploaded by anyone seated at a table, a player included,
 * into their own library as a token picture (`?for=hotbar`). It is fetched with the bearer like every picture, and only
 * its uploader and the DM (through the box) may read it.
 */
export async function uploadBoxPicture(name: string, file: Blob, fetchImpl: typeof fetch = fetch): Promise<UploadResult> {
  try {
    const res = await authFetch(`/api/assets?kind=token&for=hotbar&name=${encodeURIComponent(name)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream', Accept: 'application/json' },
      body: file,
    }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { asset?: AssetInfo; warning?: string | null; error?: string; reason?: string } | null;
    if (res.status === 201 && body?.asset) return { kind: 'ok', asset: body.asset, warning: body.warning ?? null };
    if (body?.error === 'refused' && typeof body.reason === 'string') return { kind: 'refused', reason: body.reason };
    if (res.status === 403) return { kind: 'refused', reason: 'Pictures for your boxes can be uploaded once you sit at a table.' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

/** The library, narrowed to one tag and ordered when asked (issue 1036): both are query parameters of the one route. */
export async function listAssets(fetchImpl: typeof fetch = fetch, opts: { tag?: string; sort?: LibrarySort } = {}): Promise<AssetInfo[]> {
  const q = new URLSearchParams();
  if (opts.tag) q.set('tag', opts.tag);
  if (opts.sort) q.set('sort', opts.sort);
  const { status, body } = await requestJson(`/api/assets${q.size ? `?${q}` : ''}`, { method: 'GET' }, fetchImpl);
  const assets = (body as { assets?: unknown } | null)?.assets;
  return status === 200 && Array.isArray(assets) ? (assets as AssetInfo[]) : [];
}

/** Replace a picture's tags (issue 1036): PUT, the whole list. The picture as it now stands, or null when refused. */
export async function setAssetTags(id: string, tags: string[], fetchImpl: typeof fetch = fetch): Promise<AssetInfo | null> {
  const { status, body } = await requestJson(`/api/assets/${id}/tags`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tags }) }, fetchImpl);
  const asset = (body as { asset?: AssetInfo } | null)?.asset;
  return status === 200 && asset ? asset : null;
}

export type DeleteResult = { kind: 'ok' } | { kind: 'in_use'; uses: AssetUse[] } | { kind: 'in_use_elsewhere'; tables: string[] } | { kind: 'error' };

/** Plain, a picture in use is refused with where; `everywhere` (issue 1020) takes it off every table the DM runs first. */
export async function deleteAsset(id: string, opts: { everywhere?: boolean } = {}, fetchImpl: typeof fetch = fetch): Promise<DeleteResult> {
  const { status, body } = await requestJson(`/api/assets/${id}${opts.everywhere ? '?everywhere=1' : ''}`, { method: 'DELETE' }, fetchImpl);
  if (status === 204) return { kind: 'ok' };
  const answer = body as { error?: string; uses?: AssetUse[]; tables?: string[] } | null;
  if (status === 409 && answer?.error === 'in_use_elsewhere') return { kind: 'in_use_elsewhere', tables: answer.tables ?? [] };
  if (status === 409) return { kind: 'in_use', uses: answer?.uses ?? [] };
  return { kind: 'error' };
}

/** Where a picture is in use, in words for the DM (design/23 §5). */
export function describeUses(uses: AssetUse[]): string {
  return uses.map((u) => {
    const parts: string[] = [];
    if (u.asMap) parts.push('the map');
    if (u.objects > 0) parts.push(`${u.objects} thing${u.objects === 1 ? '' : 's'}`);
    if (u.undeleted > 0) parts.push(`${u.undeleted} in your undo`);
    return `${parts.join(' and ')} at ${u.tableName}`;
  }).join('; ');
}

/** One table's use, for the delete-everywhere confirmation (issue 1020): its staged scenes are counted in. */
export function describeUse(u: AssetUse): string {
  const parts: string[] = [];
  if (u.objects > 0) parts.push(`${u.objects} token${u.objects === 1 ? '' : 's'}`);
  if (u.asMap) parts.push('its map');
  // A deleted token waiting in the DM's undo still wears the picture; the board shows none, so it is named apart.
  if (u.undeleted > 0) parts.push(`${u.undeleted} deleted token${u.undeleted === 1 ? '' : 's'} in your undo`);
  return `${u.tableName}: ${parts.join(' and ')}`;
}

const blobs = new Map<string, Promise<Blob | null>>();
const urls = new Map<string, Promise<string | null>>();

/**
 * The picture's bytes for this tab, fetched once with the bearer. A refusal
 * (a picture this person may not see, or none) is null, and is not
 * remembered: a thing revealed later fetches again. The Blob itself is
 * kept because a `blob:` URL cannot be fetched back on the deployed origin
 * — the CSP's `connect-src` does not name `blob:` (the grid detector found
 * this on vtt-dev, 2026-09-26) — so anything that needs the bytes, rather
 * than an image to draw, asks here.
 */
export function pictureBlob(id: string, fetchImpl: typeof fetch = fetch, version?: number): Promise<Blob | null> {
  const key = pictureKey(id, version);
  const have = blobs.get(key);
  if (have) return have;
  const loading = (async () => {
    try {
      const res = await authFetch(`/api/assets/${key}`, {}, fetchImpl);
      if (!res.ok) return null;
      return await res.blob();
    } catch {
      return null;
    }
  })();
  blobs.set(key, loading);
  loading.then((b) => { if (b === null) blobs.delete(key); });
  return loading;
}

/**
 * The picture as an object URL for this tab, for an image to draw: made once from the bytes.
 * `version` is the fog's (issue 851): a player's map on a fogged scene is served MASKED, and
 * changes whenever the fog does, so it is asked as `?v=<fog.rev>` and cached per version — a new
 * version fetches again, and once it has arrived the older versions of that picture are let go.
 */
export function pictureUrl(id: string, fetchImpl: typeof fetch = fetch, version?: number): Promise<string | null> {
  const key = pictureKey(id, version);
  const have = urls.get(key);
  if (have) return have;
  const loading = pictureBlob(id, fetchImpl, version).then((b) => (b ? URL.createObjectURL(b) : null));
  urls.set(key, loading);
  loading.then((url) => {
    if (url === null) { urls.delete(key); return; }
    if (version === undefined) return;
    for (const k of [...urls.keys()]) if (k !== key && k.startsWith(`${id}?v=`)) forgetKey(k);
  });
  return loading;
}

const pictureKey = (id: string, version?: number): string => (version === undefined ? id : `${id}?v=${version}`);
function forgetKey(key: string): void {
  const have = urls.get(key);
  urls.delete(key);
  blobs.delete(key);
  have?.then((url) => { if (url) URL.revokeObjectURL(url); });
}

/** Forget a picture, every version of it (after it was deleted, or when the board no longer wants it). */
export function forgetPicture(id: string): void {
  for (const k of [...new Set([...urls.keys(), ...blobs.keys()])]) if (k === id || k.startsWith(`${id}?v=`)) forgetKey(k);
}

// ── A player's map in tiles (issue 1144, slice 3; design/30 §5.3) ──────────────────────────────────────────────
// One slot per tile of a picture, holding the hash it was asked at: the same hash is the same copy, never asked again;
// a new hash asks `/api/assets/<id>/tile/<i>,<j>?v=<hash>` through `authFetch` (never a public URL) and, once the new
// copy has arrived, lets the old one go — as `pictureUrl` does for the masked whole map's versions.
const tileSlots = new Map<string, { hash: string; url: Promise<string | null> }>();
const revoke = (p: Promise<string | null>) => { void p.then((u) => { if (u) URL.revokeObjectURL(u); }); };

/** Tile `key` ("i,j") of picture `id` at `hash`, as an object URL for this tab, or null when it is not to be had. */
export function tileUrl(id: string, key: string, hash: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  const slot = `${id}/${key}`;
  const have = tileSlots.get(slot);
  if (have && have.hash === hash) return have.url;
  const url = (async () => {
    try {
      const res = await authFetch(`/api/assets/${id}/tile/${key}?v=${encodeURIComponent(hash)}`, {}, fetchImpl);
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  })();
  tileSlots.set(slot, { hash, url });
  void url.then((u) => {
    if (have) revoke(have.url); // the old copy goes once the new one is here
    if (u === null && tileSlots.get(slot)?.url === url) tileSlots.delete(slot);
  });
  return url;
}

/** Let go of a picture's tiles, all of them or every one not in `keep`. */
export function releaseTiles(id: string, keep: ReadonlySet<string> = new Set()): void {
  for (const [slot, t] of [...tileSlots]) {
    if (!slot.startsWith(`${id}/`) || keep.has(slot.slice(id.length + 1))) continue;
    tileSlots.delete(slot);
    revoke(t.url);
  }
}

/** Let go of every tile of every picture but `id` (none: all of them) — a board's map switch, or the board going. */
export function releaseTilesExcept(id: string | null): void {
  for (const [slot, t] of [...tileSlots]) {
    if (id !== null && slot.startsWith(`${id}/`)) continue;
    tileSlots.delete(slot);
    revoke(t.url);
  }
}

// ── A big map's pyramid (issue 1172) ─────────────────────────────────────────────────────────────────────────────
// Tile "k/x,y" of a big map, fetched through `authFetch` (never a public URL) as an object URL for this tab. Its bytes
// never change, so one copy per tile; `releasePyramidTile` revokes it when the board lets the tile go.
const pyramidSlots = new Map<string, Promise<string | null>>();
export function pyramidTileUrl(id: string, key: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  const slot = `${id}/${key}`;
  const have = pyramidSlots.get(slot);
  if (have) return have;
  const url = (async () => {
    try {
      const res = await authFetch(`/api/assets/${id}/pyramid/${key}`, {}, fetchImpl);
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  })();
  pyramidSlots.set(slot, url);
  void url.then((u) => { if (u === null && pyramidSlots.get(slot) === url) pyramidSlots.delete(slot); });
  return url;
}
/** Let go of one pyramid tile, or every tile of a map (no key). */
export function releasePyramidTile(id: string, key?: string): void {
  for (const [slot, url] of [...pyramidSlots]) {
    if (key ? slot !== `${id}/${key}` : !slot.startsWith(`${id}/`)) continue;
    pyramidSlots.delete(slot);
    revoke(url);
  }
}
/** How many pyramid tiles this tab holds (the `?perf=1` readout). */
export const pyramidHeld = (): number => pyramidSlots.size;

/**
 * A scene's map (issue 834): always the scene named, the one on the DM's screen (qa-runs#1465). With none named — the DM
 * looking through a player's eyes has no scene of their own on screen — nothing is sent, so a change never falls to
 * whichever scene is live.
 */
export async function setTableMap(campaignId: string, assetId: string | null, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<TableResult> {
  if (!sceneId) return { kind: 'refused', error: 'no_scene', reason: 'Leave the player\'s view first: a map is set on the scene on your screen.' };
  const { status, body } = await requestJson(`/api/tables/${campaignId}/map`, { method: 'PUT', body: JSON.stringify({ asset_id: assetId, scene_id: sceneId }) }, fetchImpl);
  if (status === 200) return { kind: 'ok', view: body as TableView };
  if (status === 404) return { kind: 'not-found' };
  if (status === 0) return { kind: 'error' };
  const err = (body as { error?: unknown } | null)?.error;
  return { kind: 'refused', error: typeof err === 'string' ? err : `http_${status}` };
}

export async function fetchView(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<BoardView | null> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/view`, { method: 'GET' }, fetchImpl);
  const view = (body as { view?: BoardView | null } | null)?.view;
  return status === 200 && view ? view : null;
}

export async function saveView(campaignId: string, view: BoardView, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson(`/api/tables/${campaignId}/view`, { method: 'PUT', body: JSON.stringify(view) }, fetchImpl);
  return status === 200;
}
